import {
  format,
  isBefore,
  differenceInDays,
  differenceInYears,
  differenceInMonths,
} from 'date-fns'

export const isConsultantListedSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].listed
  } catch (e) {
    return undefined
  }
}

export const consultantAvailabilityValueSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].availability
  } catch (e) {
    return undefined
  }
}

export const consultantAvailabilitySelector = (state, consultantId) => {
  try {
    const availability = state.consultants[consultantId].availability
    if (availability === undefined)
      return { text: 'Spør om tilgjengelighet', color: 'yellow' }
    switch (availability.type) {
      case 'date':
        return {
          text: `Tilgjengelig`,
          date: isBefore(availability.date, new Date())
            ? 'nå'
            : format(availability.date, 'DD.MM.YY'),
          color: 'green',
        }
      case 'unavailable':
        return {
          text: `Utilgjengelig`,
          color: 'red',
        }
      case 'now':
        return {
          text: `Tilgjengelig nå`,
          color: 'green',
        }
      case 'other':
        return {
          text: availability.value,
          color: 'yellow',
        }
      default:
        return { text: 'Spør om tilgjengelighet', color: 'yellow' }
    }
  } catch (e) {
    return undefined
  }
}

export const searchResultsSelector = state =>
  state.search.results[state.search.query]

export const relevanceSelector = (state, consultantId) => {
  try {
    return searchResultsSelector(state).find(
      result => result.id === consultantId
    ).matches
  } catch (e) {
    return undefined
  }
}

export const experienceTimeSelector = (state, consultantId) => {
  try {
    const date = state.consultants[consultantId].startDate
    if (!date) return ''
    const years = differenceInYears(new Date(), date)
    const months = differenceInMonths(new Date(), date) % 12
    return years
      ? years < 5 ? `${years} år, ${months} mnd` : `${years} år`
      : months ? `${months} mnd` : 'nyutdannet'
  } catch (e) {
    return undefined
  }
}

const durationText = ({ duration }) =>
  duration

const mapHobbyToExperienceInfo = hobby => ({
  type: 'hobby',
  logo: `${hobby.name}.png`,
  title: hobby.name,
  duration: durationText(hobby),
  subtitle: 'på fritiden',
  heading: (hobby.roles || []).join(', '),
  text: hobby.description,
  tags: hobby.tags,
})

const mapEmploymentToExperienceInfo = work => ({
  type: 'employment',
  logo: `${work.employer}.png`,
  title: work.employer,
  duration: durationText(work),
  heading: (work.roles || []).join(', '),
  text: work.description,
  tags: work.tags,
})

const mapWorkToExperienceInfo = work => ({
  type: 'work',
  logo: `${work.customer || work.employer}.png`,
  title: work.customer || work.employer,
  duration: durationText(work),
  subtitle:
    work.customer && work.employer
      ? work.employer.toLowerCase() === 'selvstendig konsulent' ||
        work.employer.toLowerCase() === 'student'
        ? `som ${work.employer.toLowerCase()}`
        : `for ${work.employer}`
      : undefined,
  heading: `${work.name || ''}${work.name && work.roles ? ' · ' : ''}${(
    work.roles || []
  ).join(', ')}`,
  text: work.description,
  tags: work.tags,
})

const mapEducationToExperienceInfo = education => ({
  type: 'education',
  logo: `${education.university}.png`,
  title: education.university,
  duration: durationText(education),
  heading: education.degree,
  text: education.description,
})

export const experienceInfoSelector = (state, consultantId, experienceId) => {
  try {
    const experience = state.consultants[consultantId].experience.get(
      experienceId
    )
    switch (experience.type) {
      case 'employment':
        return mapEmploymentToExperienceInfo(experience)
      case 'work':
        return mapWorkToExperienceInfo(experience)
      case 'education':
        return mapEducationToExperienceInfo(experience)
      default:
        return mapHobbyToExperienceInfo(experience)
    }
  } catch (e) {
    return undefined
  }
}

export const consultantExperienceIdsSelector = (state, consultantId) => {
  try {
    const experience = state.consultants[consultantId].experience
    return experience
      .sort(
        (a, b) =>
          a.endDate === b.endDate
            ? differenceInDays(b.startDate, a.startDate)
            : differenceInDays(b.endDate || new Date(), a.endDate || new Date())
      )
      .keySeq()
  } catch (e) {
    return undefined
  }
}

export const consultantNameSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].name
  } catch (e) {
    return undefined
  }
}

export const consultantMobileSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].mobile
  } catch (e) {
    return undefined
  }
}

export const consultantDateOfBirthSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].dateOfBirth
  } catch (e) {
    return undefined
  }
}

export const consultantFirstNameSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].name.split(' ')[0]
  } catch (e) {
    return undefined
  }
}

export const consultantEmployerSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].employer
  } catch (e) {
    return undefined
  }
}

export const consultantTitlesSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].titles
  } catch (e) {
    return undefined
  }
}

export const consultantSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId]
  } catch (e) {
    return {}
  }
}

export const consultantInterestsSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].interests
  } catch (e) {
    return undefined
  }
}

export const consultantIsPendingSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].pending
  } catch (e) {
    return true
  }
}

export const consultantStartDateSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].startDate
  } catch (e) {
    return undefined
  }
}

export const consultantRolesSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].roles
  } catch (e) {
    return undefined
  }
}

export const consultantProfilePictureSelector = (state, consultantId) => {
  try {
    return state.consultants[consultantId].profilePicture
  } catch (e) {
    return undefined
  }
}



// WEBPACK FOOTER //
// ./src/selectors/consultant.js